import { useState, useEffect } from 'react'
import type { Category, FamilyMember } from '@/types/database'

interface QuickAddFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  defaultType?: "income" | "expense";
}

export function QuickAddForm({
  isOpen,
  onClose,
  onSuccess,
  defaultType = "expense",
}: QuickAddFormProps) {
  const [type, setType] = useState<"income" | "expense">(defaultType);
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [categoryId, setCategoryId] = useState("");
  const [familyMemberId, setFamilyMemberId] = useState("");
  const [description, setDescription] = useState("");
  const [isRecurring, setIsRecurring] = useState(false);

  const [categories, setCategories] = useState<Category[]>([]);
  const [familyMembers, setFamilyMembers] = useState<FamilyMember[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen) {
      fetchFormData();
    }
  }, [isOpen, type]);

  async function fetchFormData() {
    try {
      const [categoriesRes, familyMembersRes] = await Promise.all([
        fetch(`/api/categories?type=${type}`),
        fetch("/api/family-members"),
      ]);

      const categoriesData = (await categoriesRes.json()) as {
        categories: Category[];
      };
      const familyMembersData = (await familyMembersRes.json()) as {
        familyMembers: FamilyMember[];
      };

      setCategories(categoriesData.categories || []);
      setFamilyMembers(familyMembersData.familyMembers || []);
    } catch (err) {
      console.error("Error fetching form data:", err);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      const response = await fetch("/api/transactions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type,
          amount: parseFloat(amount),
          date,
          category_id: parseInt(categoryId, 10),
          family_member_id: parseInt(familyMemberId, 10),
          description: description || undefined,
          is_recurring: isRecurring,
        }),
      });

      if (!response.ok) {
        const data = (await response.json()) as { error?: string };
        throw new Error(data.error || "Failed to add transaction");
      }

      // Reset form and close
      resetForm();
      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setIsLoading(false);
    }
  }

  function resetForm() {
    setAmount("");
    setDate(new Date().toISOString().split("T")[0]);
    setCategoryId("");
    setFamilyMemberId("");
    setDescription("");
    setIsRecurring(false);
  }

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-charcoal/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-charcoal">
            Quick Add Transaction
          </h2>
          <button
            onClick={onClose}
            className="text-stone hover:text-charcoal transition-colors"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="bg-danger/10 text-danger px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Type Toggle */}
          <div className="flex rounded-lg border border-stone/30 overflow-hidden">
            <button
              type="button"
              onClick={() => setType("expense")}
              className={`flex-1 py-2 text-sm font-medium transition-colors ${
                type === "expense"
                  ? "bg-danger text-white"
                  : "bg-white text-charcoal hover:bg-cream"
              }`}
            >
              💸 Expense
            </button>
            <button
              type="button"
              onClick={() => setType("income")}
              className={`flex-1 py-2 text-sm font-medium transition-colors ${
                type === "income"
                  ? "bg-success text-white"
                  : "bg-white text-charcoal hover:bg-cream"
              }`}
            >
              💰 Income
            </button>
          </div>

          {/* Amount */}
          <div>
            <label className="block text-sm font-medium text-charcoal mb-1">
              Amount
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone">
                $
              </span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full pl-8 pr-4 py-2 rounded-lg border border-stone/30 focus:border-sage focus:ring-2 focus:ring-sage/20 outline-none"
                placeholder="0.00"
                required
              />
            </div>
          </div>

          {/* Date */}
          <div>
            <label className="block text-sm font-medium text-charcoal mb-1">
              Date
            </label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full px-4 py-2 rounded-lg border border-stone/30 focus:border-sage focus:ring-2 focus:ring-sage/20 outline-none"
              required
            />
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-charcoal mb-1">
              Category
            </label>
            <select
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              className="w-full px-4 py-2 rounded-lg border border-stone/30 focus:border-sage focus:ring-2 focus:ring-sage/20 outline-none"
              required
            >
              <option value="">Select category...</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.icon} {cat.name}
                </option>
              ))}
            </select>
          </div>

          {/* Family Member */}
          <div>
            <label className="block text-sm font-medium text-charcoal mb-1">
              Family Member
            </label>
            <select
              value={familyMemberId}
              onChange={(e) => setFamilyMemberId(e.target.value)}
              className="w-full px-4 py-2 rounded-lg border border-stone/30 focus:border-sage focus:ring-2 focus:ring-sage/20 outline-none"
              required
            >
              <option value="">Select person...</option>
              {familyMembers.map((fm) => (
                <option key={fm.id} value={fm.id}>
                  {fm.name}
                </option>
              ))}
            </select>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-charcoal mb-1">
              Description (optional)
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-4 py-2 rounded-lg border border-stone/30 focus:border-sage focus:ring-2 focus:ring-sage/20 outline-none"
              placeholder="What was this for?"
              maxLength={255}
            />
          </div>

          {/* Recurring */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="recurring"
              checked={isRecurring}
              onChange={(e) => setIsRecurring(e.target.checked)}
              className="rounded border-stone/30"
            />
            <label htmlFor="recurring" className="text-sm text-charcoal">
              This is a recurring {type}
            </label>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 px-4 border border-stone/30 rounded-lg text-charcoal hover:bg-cream transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 py-2 px-4 bg-forest text-white rounded-lg hover:bg-forest-dark disabled:opacity-50 transition-colors"
            >
              {isLoading ? "Adding..." : "Add Transaction"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
